import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DateTime } from 'luxon';
import { QuillViewComponent } from 'ngx-quill';
import { BaseDialogComponent } from 'app/components/base-dialog/base-dialog.component';
import { UsersClient, WhatsNewDto } from 'app/core/services/api.service';
import { first, of, switchMap, tap } from 'rxjs';
import { LuxonFormatPipe } from '../../../shared/pipes/luxon-format.pipe';

@Component({
	selector: 'cm-whats-new',
	templateUrl: './whats-new.component.html',
	styleUrls: ['./whats-new.component.scss'],
	imports: [
		CommonModule,
		BaseDialogComponent,
		MatButtonModule,
		MatIconModule,
		MatProgressSpinnerModule,
		QuillViewComponent,
		LuxonFormatPipe,
	],
})
export class WhatsNewComponent implements OnInit {
	readonly notifications = signal<WhatsNewDto[]>([]);
	readonly loading = signal(true);
	readonly expandedNotifications = signal<Set<number>>(new Set([0]));

	readonly hasNotifications = computed(() => this.notifications().length > 0);
	readonly unreadCount = computed(() => this.notifications().filter((n) => !n.dateRead).length);

	constructor(
		private _dialogRef: MatDialogRef<WhatsNewComponent>,
		private _usersClient: UsersClient
	) {}

	ngOnInit(): void {
		this._dialogRef
			.afterOpened()
			.pipe(
				first(),
				switchMap(() => this._usersClient.getWhatsNew()),
				switchMap((whatsNew) => {
					this.notifications.set(whatsNew);
					this.loading.set(false);
					this._setInitialExpandedNotification(whatsNew);

					if (!whatsNew.some((notification) => !notification.dateRead)) {
						return of(void 0);
					}

					return this._usersClient.markAsReadWhatsNew().pipe(
						tap(() => this._markNotificationsReadLocally())
					);
				})
			)
			.subscribe();
	}

	isNotificationExpanded(index: number): boolean {
		return this.expandedNotifications().has(index);
	}

	toggleNotification(index: number): void {
		const expanded = new Set(this.expandedNotifications());
		if (expanded.has(index)) {
			expanded.delete(index);
		} else {
			expanded.add(index);
		}
		this.expandedNotifications.set(expanded);
	}

	onClose(): void {
		this._dialogRef.close();
	}

	private _setInitialExpandedNotification(whatsNew: WhatsNewDto[]): void {
		const firstUnreadIndex = whatsNew.findIndex((notification) => !notification.dateRead);
		const indexToExpand = firstUnreadIndex >= 0 ? firstUnreadIndex : 0;
		if (whatsNew.length > 0) {
			this.expandedNotifications.set(new Set([indexToExpand]));
		}
	}

	private _markNotificationsReadLocally(): void {
		const dateRead = DateTime.utc();
		this.notifications.update((notifications) =>
			notifications.map((notification) => {
				if (notification.dateRead) {
					return notification;
				}

				return WhatsNewDto.fromJS({
					...notification.toJSON(),
					dateRead: dateRead.toISO(),
				});
			})
		);
	}
}

